import React, { useCallback } from "react";
import styles from "./BtnCellRenderer.module.css";
import { downloadEngagementLetter } from "./BtnCellRenderer.service";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Icon from "react-bootstrap-icons";
import { getFromLS } from "../../../../../utils/storage.js";

/**
   * Button cell renderer for the grid.
   * @param {Object} props - Props for the button cell renderer.
   * @returns Button for downloading and viewing the engagement letter.
   */
const BtnCellRenderer: React.FC = (props: any) => {
  const availableFeatures = getFromLS("active-features")
  const showELSummary = Array.isArray(availableFeatures)
    ? availableFeatures.some((str: string) => str.includes("EL_SET_SUMMARY"))
    : false;

  
  const toastBasicConfig = {
    position: "bottom-right",
    autoClose: 2500,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  };

  const toastErrorConfig: any = {
    ...toastBasicConfig,
    className: "toastify-color-error",
  };

  const getEngagementLetter = useCallback(async (fileName: any, containerName: any) => {
    try {
      return await downloadEngagementLetter(fileName, containerName)
    } catch (error) {
      console.error(error);
    }
  }, []);

  const viewESignedEngagementLetterClick = async () => {
    try {
      const blob = await getEngagementLetter(props.data.FileName, "signeddocumentfiles");
      const url = window.URL.createObjectURL(blob);

      props.setPdfFileUrl(url);

      props.setTemplatePreviewModal(true);
    } catch (error) {
      toast.error(
        "The file is not ready yet. Please try again later.",
        toastErrorConfig
      );
    }
  };

  const viewClick = async () => {
    try {
      const blob = await getEngagementLetter(props.data.FileName, "engagementletterfiles");
      const url = window.URL.createObjectURL(blob);

      props.setPdfFileUrl(url);

      props.setTemplatePreviewModal(true);
    } catch (error) {
      console.error('Error viewing engagement letter:', error);
    }
  };

  const editClick = () => {
    if (props.data.Status === "Draft") {
      props.openDraftWizard?.({
        processedTemplateId: props.data.ProcessedTemplateId,
        templateId: props.data.TemplateId,
        clientMasterId: props.data.ClientMasterId,
      });
      return;
    }

    localStorage.setItem('processed_template_id', props.data.ProcessedTemplateId);

    const docFilename = props.data.DocFileName;
    const filenameWithoutExtension = docFilename.substring(0, docFilename.lastIndexOf('.')) || docFilename;
    localStorage.setItem("key", filenameWithoutExtension);

    props.setDocFileName(docFilename);

    localStorage.setItem('letter_file_name', props.data.FileName);

    localStorage.setItem('doc_original_file_name', "");

    localStorage.setItem('recipients', props.data.Recipients);
    localStorage.setItem('signatureFields', props.data.FrontendSignatureDataList);

    props.setRecipientsModalData(props.data.RecipientsJson);

    localStorage.setItem('isEdit', JSON.stringify(true));
    localStorage.setItem('edit_type', "EngagementLetter");

    const filteredClientData = Object.fromEntries(
      Object.entries(props.data).filter(([key, value]) =>
        key.toLowerCase().includes('email')
      )
    );

    localStorage.setItem('client_details', JSON.stringify(filteredClientData));

    props.setCreateOrEditEditorComponentShow(true);
  }

  const not_allow_statuses = ["Initialized", "Processing", "In-Process"]

  const enable_buttons = !not_allow_statuses.includes(props.data.Status);

  const approvalBtnClick = () => {
    localStorage.setItem('processed_template_id', props.data.ProcessedTemplateId);
    props.setClientMasterId(props.data.ClientMasterId);
    props.setApprovalSettingModal(true);
  }

  const summaryBtnClick = () => {
    props.setELId(props.data.ProcessedTemplateId);
    props.setELFileName(props.data.FileName);
    props.setIsSummaryModalOpen(true);
  }
  
  const wetSignUploadBtnClick = () => {
    props.setShowWetSignModal?.(true);
    props.setELData?.(props.data);
  }

  const hideWetSign = (
    ["wetsign completed", "e-signed"].includes(
      String(props.data.SignatureStatus).toLowerCase()
    )
  );

  const nonEditableSignatureStatuses = [
    "manually signed",
    "partially signed",
    "signed",
    "wetsign completed",
    'e-signed'
  ];
  const isEditDisabledBySignature = nonEditableSignatureStatuses.includes(
    String(props.data.SignatureStatus).toLowerCase()
  );

  const canEdit = enable_buttons && !isEditDisabledBySignature;

  return (
    <div>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={
          <Tooltip id="button-tooltip">
            {!enable_buttons
              ? "Document not ready"
              : isEditDisabledBySignature
                ? "Editing is not allowed once signing has started"
                : props.data.Status === "Draft"
                  ? "Resume Draft"
                  : "Edit letter"}
          </Tooltip>
        }
      >
        <div
          className={styles.tooltipWrapper}
          style={{
            cursor: canEdit ? "pointer" : "not-allowed",
          }}
        >
          <Button
            className={styles.actionButton}
            style={{
              pointerEvents: canEdit ? "all" : "none",
            }}
            disabled={!canEdit}
            onClick={() => editClick()}
          >
            <Icon.PencilSquare />
          </Button>
        </div>
      </OverlayTrigger>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={
          <Tooltip id="button-tooltip">
            {enable_buttons
              ? "View document"
              : "Document not ready"
            }
          </Tooltip>
        }
      >
        <span className={styles.tooltipWrapper}>
          <Button
            style={{
              pointerEvents: (
                enable_buttons ? "all" : "none"
              ),
            }}
            disabled={!enable_buttons}
            className={styles.actionButton}
            onClick={() => viewClick()}
          >
            <Icon.Eye />
          </Button>
        </span>
      </OverlayTrigger>
      {showELSummary &&
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={
            <Tooltip id="button-tooltip">
              {enable_buttons
                ? "Generate summary and key insights"
                : "Document not ready"
              }
            </Tooltip>
          }
        >
          <div className={styles.tooltipWrapper} style={{
            cursor: enable_buttons ? "pointer" : "not-allowed",
          }}>
            <Button
              className={styles.actionButton}
              style={{
                pointerEvents: (
                  enable_buttons ? "all" : "none"
                ),
              }}
              disabled={!enable_buttons}
              onClick={summaryBtnClick}>
              <Icon.Magic />
            </Button>
          </div>
        </OverlayTrigger>
      }
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={
          <Tooltip id="button-tooltip">
            {props.data.ApprovalStatus === "Draft"
              ? "Configure approval settings"
              : "Editable only when approval status is 'Draft'"
            }
          </Tooltip>
        }
      >
        <div className={styles.tooltipWrapper} style={{
          cursor: props.data.ApprovalStatus !== "Draft" || !enable_buttons ? "not-allowed" : "pointer",
        }}>
          <Button
            className={styles.actionButton}
            style={{
              pointerEvents: (
                props.data.ApprovalStatus !== "Draft" || !enable_buttons ? "none" : "all"
              )
            }}
            disabled={props.data.ApprovalStatus !== "Draft" || !enable_buttons}
            onClick={() => approvalBtnClick()}>
            <Icon.Gear />
          </Button>
        </div>
      </OverlayTrigger>
        
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={
          <Tooltip id="button-tooltip">
            {
              !hideWetSign
                ? "Upload WetSigned EL"
                : "This document is already signed"
            }
          </Tooltip>
        }
      >
        <div
          className={styles.tooltipWrapper}
          style={{
            cursor:
              !hideWetSign
                ? "pointer"
                : "not-allowed",
          }}
        >
          <Button
            className={styles.actionButton}
            disabled={
              hideWetSign
            }
            style={{
              pointerEvents:
                !hideWetSign
                  ? "all"
                  : "none",
            }}
            onClick={wetSignUploadBtnClick}
          >
            <Icon.CloudArrowUp />
          </Button>
        </div>
      </OverlayTrigger>

      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={
          <Tooltip id="button-tooltip">
            {["E-Signed", "WetSign Completed"].includes(props.data.SignatureStatus)
              ? "View Signed document"
              : "Document not Signed"}
          </Tooltip>
        }
      >
        <div
          className={styles.tooltipWrapper}
          style={{
            cursor: ["E-Signed", "WetSign Completed"].includes(props.data.SignatureStatus) ? "pointer" : "not-allowed",
          }}
        >
          <Button
            className={styles.actionButton}
            style={{
              pointerEvents: ["E-Signed", "WetSign Completed"].includes(props.data.SignatureStatus) ? "all" : "none",
            }}
            disabled={!["E-Signed", "WetSign Completed"].includes(props.data.SignatureStatus)}
            onClick={() => viewESignedEngagementLetterClick()}
          >
            <Icon.FileEarmarkText />
          </Button>
        </div>
      </OverlayTrigger>
    </div>
  );
};

export default BtnCellRenderer;
